import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background, Controls, MiniMap,
  applyNodeChanges, applyEdgeChanges, addEdge, useReactFlow, ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Modal } from 'antd';
import {
  MessageOutlined, FormOutlined, RobotOutlined, StopOutlined,
  ArrowLeftOutlined, SaveOutlined, DeleteOutlined, PlusOutlined,
  ThunderboltOutlined, GlobalOutlined, PlayCircleOutlined,
  CheckOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { botFlowApi } from './services/botFlowApi';
import BotNodeView from './BotNodeView';
import FlowPreviewPanel from './FlowPreviewPanel';

// ─── Auto-layout helper (dagre) ──────────────────────────────────────────
// Lays out the graph in clean top-to-bottom (or left-to-right) ranks.
// Uses dagre's layered algorithm — same approach as n8n / Manychat.
const LAYOUT_NODE_W = 260;
const LAYOUT_NODE_H = 120;
function getLayoutedElements(nodes, edges, direction = 'TB') {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    nodesep: 70,
    ranksep: 110,
    marginx: 40,
    marginy: 40,
  });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: LAYOUT_NODE_W, height: LAYOUT_NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  const isHoriz = direction === 'LR';
  return nodes.map((n) => {
    const p = g.node(n.id);
    if (!p) return n;
    return {
      ...n,
      position: { x: p.x - LAYOUT_NODE_W / 2, y: p.y - LAYOUT_NODE_H / 2 },
      targetPosition: isHoriz ? 'left' : 'top',
      sourcePosition: isHoriz ? 'right' : 'bottom',
    };
  });
}

const NODE_TYPES = { bot: BotNodeView };
const DEFAULT_NODE_POSITION = { x: 100, y: 100 };

const NEW_NODE_DEFAULTS = {
  message:    { text: 'Hello! Choose an option:', options: [{ id: 'opt_1', label: 'Option 1' }] },
  input:      { text: 'Please enter your name:', field_name: 'name' },
  ai_handoff: { text: 'Switching you to our AI assistant. Ask anything!' },
  api_call:   {
    url: 'https://api.example.com/resource/{{id}}',
    method: 'GET',
    auth: { type: 'none' },
    headers: {},
    response_map: {},
    success_text: '',
    error_text: "Sorry, this action is not available right now.",
    timeout_ms: 8000,
  },
  end:        { text: 'Thanks! Have a great day.' },
};

const NODE_TYPE_LABELS = {
  message: 'Message',
  input: 'Input',
  ai_handoff: 'AI Handoff',
  api_call: 'API Call',
  end: 'End',
};

const NODE_TYPE_ALIASES = {
  ask_input: 'input',
  ask_for_input: 'input',
  user_input: 'input',
  ai: 'ai_handoff',
  handoff: 'ai_handoff',
  ai_handoff: 'ai_handoff',
  ai_handoff_node: 'ai_handoff',
  api: 'api_call',
  api_call: 'api_call',
  api_call_node: 'api_call',
  apicall: 'api_call',
  stop: 'end',
  finish: 'end',
};

const API_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const API_BODY_METHODS = ['POST', 'PUT', 'PATCH'];
const BLOCKED_HEADER_KEYS = ['proxy-authorization', 'x-forwarded-for', 'x-real-ip', 'host'];
const API_AUTH_TYPES = ['none', 'bearer', 'basic', 'api_key'];
const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);
const cloneJson = (value) => JSON.parse(JSON.stringify(value || {}));

const getEdgeVisual = (condition) => {
  if (condition === 'success') {
    return {
      labelStyle: { fontSize: 10, fill: '#15803d' },
      style: { stroke: '#16a34a', strokeWidth: 1.8 },
      animated: false,
    };
  }
  if (condition === 'error') {
    return {
      labelStyle: { fontSize: 10, fill: '#b91c1c' },
      style: { stroke: '#dc2626', strokeWidth: 1.8 },
      animated: false,
    };
  }
  return {
    labelStyle: { fontSize: 10, fill: '#4f46e5' },
    style: { stroke: '#4f46e5', strokeWidth: 1.5 },
    animated: false,
  };
};

const normalizeUrlForValidation = (url) => String(url || '').replace(/\{\{\s*[-\w.]+\s*\}\}/g, 'value');

const isBlockedApiHost = (hostname) => {
  const host = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  if (host === 'localhost') return true;
  if (host.endsWith('.localhost')) return true;
  if (host === '0.0.0.0') return true;
  if (host === '::1') return true;
  if (host === '169.254.169.254') return true;
  if (/^127\./.test(host)) return true;
  if (/^10\./.test(host)) return true;
  if (/^192\.168\./.test(host)) return true;
  const match172 = host.match(/^172\.(\d{1,2})\./);
  if (match172) {
    const octet = Number(match172[1]);
    if (octet >= 16 && octet <= 31) return true;
  }
  return false;
};

const sanitizeApiAuth = (auth = {}) => {
  const type = API_AUTH_TYPES.includes(String(auth.type || '').toLowerCase())
    ? String(auth.type || '').toLowerCase()
    : 'none';
  if (type === 'bearer') {
    return { type, token: String(auth.token || '').trim() };
  }
  if (type === 'basic') {
    return {
      type,
      username: String(auth.username || '').trim(),
      password: String(auth.password || ''),
    };
  }
  if (type === 'api_key') {
    const key = String(auth.key || '').trim();
    return {
      type,
      in: auth.in === 'query' ? 'query' : 'header',
      key,
      value: String(auth.value || '').trim(),
    };
  }
  return { type: 'none' };
};

const validateApiAuth = (auth = {}) => {
  const errors = [];
  const normalized = sanitizeApiAuth(auth);
  if (!API_AUTH_TYPES.includes(normalized.type)) errors.push('Auth type must be None, Bearer, API Key, or Basic');
  if (normalized.type === 'bearer' && !normalized.token) errors.push('Bearer token is required');
  if (normalized.type === 'basic' && (!normalized.username || !normalized.password)) errors.push('Basic auth username and password are required');
  if (normalized.type === 'api_key') {
    if (!normalized.key || !normalized.value) errors.push('API key name and value are required');
    if (normalized.in === 'header' && BLOCKED_HEADER_KEYS.includes(normalized.key.toLowerCase())) {
      errors.push(`Header "${normalized.key}" is not allowed`);
    }
  }
  return errors;
};

const sanitizeApiCallContent = (content = {}) => {
  const method = API_METHODS.includes(String(content.method || '').toUpperCase())
    ? String(content.method || '').toUpperCase()
    : 'GET';
  const headers = Object.entries(isPlainObject(content.headers) ? content.headers : {}).reduce((acc, [key, value]) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) return acc;
    if (BLOCKED_HEADER_KEYS.includes(normalizedKey.toLowerCase())) return acc;
    acc[normalizedKey] = value;
    return acc;
  }, {});
  const timeout = Number(content.timeout_ms || 8000);
  const next = {
    url: String(content.url || '').trim(),
    method,
    auth: sanitizeApiAuth(content.auth),
    headers,
    response_map: isPlainObject(content.response_map) ? content.response_map : {},
    success_text: content.success_text || '',
    error_text: content.error_text || "Sorry, this action is not available right now.",
    timeout_ms: Number.isFinite(timeout) ? Math.min(timeout, 30000) : 8000,
  };
  if (API_BODY_METHODS.includes(method)) {
    next.body = content.body && typeof content.body === 'object' && !Array.isArray(content.body) ? content.body : {};
  }
  return next;
};

const validateApiCallContent = (content = {}) => {
  const errors = [];
  const method = String(content.method || 'GET').toUpperCase();
  const timeout = Number(content.timeout_ms || 8000);
  if (!String(content.url || '').trim()) errors.push('URL is required');
  if (!API_METHODS.includes(method)) errors.push('Method must be GET, POST, PUT, PATCH, or DELETE');
  if (Number.isFinite(timeout) && timeout > 30000) errors.push('Timeout cannot exceed 30 seconds');
  if (String(content.url || '').trim()) {
    try {
      const parsedUrl = new URL(normalizeUrlForValidation(content.url));
      if (!['http:', 'https:'].includes(parsedUrl.protocol) || isBlockedApiHost(parsedUrl.hostname)) {
        errors.push('Private or non-HTTP URLs are not allowed');
      }
    } catch {
      errors.push('Enter a valid HTTP or HTTPS URL');
    }
  }
  Object.keys(content.headers || {}).forEach((key) => {
    if (BLOCKED_HEADER_KEYS.includes(String(key).toLowerCase())) {
      errors.push(`Header "${key}" is not allowed`);
    }
  });
  errors.push(...validateApiAuth(content.auth));
  if (API_BODY_METHODS.includes(method) && content.body && (typeof content.body !== 'object' || Array.isArray(content.body))) {
    errors.push('Body must be a JSON object');
  }
  return errors;
};

const objectToRows = (obj = {}) => Object.entries(obj || {}).map(([key, value]) => ({
  key,
  value: value === null || value === undefined || typeof value === 'string' ? (value || '') : String(value),
}));

const normalizeKeyValueRows = (rows = []) => {
  const seenKeys = new Set();
  return rows.filter((row) => {
    const key = String(row.key || '').trim();
    if (!key) return true;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
};

const rowsToObject = (rows = []) => rows.reduce((acc, row) => {
  const key = String(row.key || '').trim();
  if (!key) return acc;
  acc[key] = row.value || '';
  return acc;
}, {});

const normalizeMessageOptions = (content = {}, defaults = {}) => {
  const rawOptions = Array.isArray(content.options)
    ? content.options
    : Array.isArray(content.buttons)
      ? content.buttons
      : (defaults.options || []);

  return rawOptions.map((option, index) => {
    const id = String(option.id || option.payload || option.value || `opt_${index + 1}`).trim();
    const label = String(option.label || option.text || option.title || id).trim();
    return { id, label };
  }).filter((option) => option.id && option.label).slice(0, 3);
};

const withMessageButtonAliases = (content = {}) => {
  const options = normalizeMessageOptions(content);
  return {
    ...content,
    options,
    buttons: options.map((option) => ({
      id: option.id,
      payload: option.id,
      value: option.id,
      label: option.label,
      text: option.label,
      title: option.label,
    })),
  };
};

const normalizeNodeType = (value, fallback = 'message') => {
  const rawType = String(value || fallback || 'message').trim();
  const normalizedType = rawType.toLowerCase().replace(/[-\s]+/g, '_');
  const aliasType = NODE_TYPE_ALIASES[normalizedType] || normalizedType;
  return NEW_NODE_DEFAULTS[aliasType] ? aliasType : 'message';
};

const formatNodeTypeLabel = (type) => {
  const normalizedType = normalizeNodeType(type);
  return NODE_TYPE_LABELS[normalizedType] || normalizedType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const extractNodeRecord = (rawNode = {}) => {
  if (!isPlainObject(rawNode)) return {};
  const nestedNode = rawNode.node || rawNode.bot_node || rawNode.botNode || rawNode.flow_node || rawNode.flowNode;
  if (isPlainObject(nestedNode)) return nestedNode;
  const nestedData = rawNode.data;
  if (
    isPlainObject(nestedData)
    && !getFirstDefined(rawNode.id, rawNode.node_id, rawNode.nodeId, rawNode._id)
    && getFirstDefined(nestedData.id, nestedData.node_id, nestedData.nodeId, nestedData._id, nestedData.node_type, nestedData.nodeType, nestedData.type)
  ) {
    return nestedData;
  }
  return rawNode;
};

const extractList = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
};

const getNodeId = (node, fallback = {}) => getFirstDefined(
  node.id,
  node.node_id,
  node.nodeId,
  node._id,
  fallback.id,
  fallback.node_id,
  fallback.nodeId,
  fallback._id,
);

const getNodeContent = (node, nodeType, fallback = {}) => {
  const dataAsContent = isPlainObject(node.data)
    && !getFirstDefined(node.data.id, node.data.node_id, node.data.nodeId, node.data.node_type, node.data.nodeType, node.data.type)
    ? node.data
    : undefined;
  const content = getFirstDefined(node.content, node.config, node.payload, dataAsContent, fallback.content);
  if (isPlainObject(content)) return normalizeNodeContent(nodeType, content);
  if (typeof content === 'string') {
    try {
      const parsedContent = JSON.parse(content);
      if (isPlainObject(parsedContent)) return normalizeNodeContent(nodeType, parsedContent);
    } catch {
      return NEW_NODE_DEFAULTS[nodeType] || {};
    }
  }
  return NEW_NODE_DEFAULTS[nodeType] || {};
};

function normalizeNodeContent(nodeType, content = {}) {
  const defaults = NEW_NODE_DEFAULTS[nodeType] || {};
  const mergedContent = { ...defaults, ...content };
  if (nodeType === 'message') {
    return withMessageButtonAliases({
      ...mergedContent,
      text: String(mergedContent.text || defaults.text || ''),
      options: normalizeMessageOptions(mergedContent, defaults),
    });
  }
  if (nodeType === 'input') {
    return {
      ...mergedContent,
      text: String(mergedContent.text || defaults.text || ''),
      field_name: String(mergedContent.field_name || defaults.field_name || 'input'),
    };
  }
  if (nodeType === 'api_call') {
    return sanitizeApiCallContent(mergedContent);
  }
  return {
    ...mergedContent,
    text: String(mergedContent.text || defaults.text || ''),
  };
}

const normalizeNodePosition = (node = {}, fallback = DEFAULT_NODE_POSITION) => {
  const rawPosition = node.position ?? node.position_json ?? node.positionJson;
  let position = rawPosition;
  if (typeof rawPosition === 'string') {
    try {
      position = JSON.parse(rawPosition);
    } catch {
      position = null;
    }
  }
  const x = Number(position?.x);
  const y = Number(position?.y);
  if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };

  const fallbackX = Number(fallback?.x);
  const fallbackY = Number(fallback?.y);
  return {
    x: Number.isFinite(fallbackX) ? fallbackX : DEFAULT_NODE_POSITION.x,
    y: Number.isFinite(fallbackY) ? fallbackY : DEFAULT_NODE_POSITION.y,
  };
};

const toFlowNode = (rawNode = {}, fallback = {}) => {
  const node = extractNodeRecord(rawNode);
  const id = getNodeId(node, fallback);
  if (id === undefined || id === null) return null;
  const nodeType = normalizeNodeType(
    getFirstDefined(node.node_type, node.nodeType, node.type, node.kind, fallback.node_type, fallback.nodeType, fallback.type),
    fallback.node_type || fallback.nodeType || fallback.type,
  );

  return {
    id: String(id),
    type: 'bot',
    position: normalizeNodePosition(node, fallback.position || fallback),
    data: {
      node_type: nodeType,
      content: getNodeContent(node, nodeType, fallback),
      is_start: !!getFirstDefined(node.is_start, node.isStart, fallback.is_start, fallback.isStart),
    },
  };
};

const getEdgeEndpoint = (edge, keys) => getFirstDefined(...keys.map((key) => edge[key]));

const toFlowEdge = (rawEdge = {}, index = 0) => {
  const edge = rawEdge.edge || rawEdge.flow_edge || rawEdge.flowEdge || rawEdge;
  if (!isPlainObject(edge)) return null;
  const source = getEdgeEndpoint(edge, ['from_node_id', 'fromNodeId', 'from', 'source_node_id', 'sourceNodeId', 'source']);
  const target = getEdgeEndpoint(edge, ['to_node_id', 'toNodeId', 'to', 'target_node_id', 'targetNodeId', 'target']);
  if (source === undefined || source === null || target === undefined || target === null) return null;
  const condition = getFirstDefined(edge.condition, edge.sourceHandle, edge.source_handle, edge.label, null);
  return {
    id: String(getFirstDefined(edge.id, edge.edge_id, edge.edgeId, `${source}-${target}-${condition || 'default'}-${index}`)),
    source: String(source),
    target: String(target),
    sourceHandle: condition || 'default',
    label: condition || '',
    ...getEdgeVisual(condition),
  };
};

const getFlowEdgeCondition = (edge = {}) => {
  const condition = getFirstDefined(edge.sourceHandle, edge.source_handle, edge.condition, edge.label, null);
  return condition && condition !== 'default' ? condition : null;
};

const toGraphNodePayload = (node = {}) => {
  const nodeType = normalizeNodeType(node.data?.node_type);
  return {
    id: node.id,
    node_type: nodeType,
    is_start: !!node.data?.is_start,
    position: normalizeNodePosition(node, node.position),
    content: normalizeNodeContent(nodeType, node.data?.content || {}),
  };
};

const toGraphEdgePayload = (edge = {}) => ({
  id: edge.id,
  from_node_id: edge.source,
  condition: getFlowEdgeCondition(edge),
  to_node_id: edge.target,
});

const toGraphFlowPayload = (flow = {}) => ({
  name: flow.name || 'Untitled flow',
  trigger_type: getFirstDefined(flow.trigger_type, flow.triggerType, 'always'),
  trigger_value: getFirstDefined(flow.trigger_value, flow.triggerValue, null),
});

const ensureOneStartNode = (graphNodes = []) => {
  if (graphNodes.length === 0 || graphNodes.some((node) => node.data?.is_start)) return graphNodes;
  return graphNodes.map((node, index) => (
    index === 0 ? { ...node, data: { ...node.data, is_start: true } } : node
  ));
};

/**
 * Pure validation function shared by preview and activation.
 * Returns { errors: [{nodeId, message}], warnings: [{nodeId, message}] }.
 * Hard errors block activation and preview; warnings are advisory.
 */  
const validateFlowForPreview = (nodes = [], edges = []) => {
  const errors = [];
  const warnings = [];

  if (nodes.length === 0) {
    errors.push({ nodeId: null, message: 'This flow has no nodes. Add at least one node before previewing.' });
    return { errors, warnings };
  }

  const startNode = nodes.find((n) => n.data?.is_start);
  if (!startNode) {
    errors.push({ nodeId: null, message: 'No start node defined. Mark one node as the start node.' });
  }

  nodes.forEach((node) => {
    const type = normalizeNodeType(node.data?.node_type);
    const content = node.data?.content || {};
    const outgoing = edges.filter((e) => e.source === node.id);

    if (type === 'api_call') {
      const apiErrors = validateApiCallContent(content);
      apiErrors.forEach((msg) => errors.push({ nodeId: node.id, message: msg }));

      if (apiErrors.length === 0) {
        const hasSuccessPath =
          Boolean((content.success_text || '').trim()) ||
          outgoing.some((e) => e.sourceHandle === 'success' || e.label === 'success');
        if (!hasSuccessPath) {
          warnings.push({ nodeId: node.id, message: `API Call node "${truncateLabel(content.url)}" has no success reply or success edge.` });
        }
        const hasErrorPath =
          Boolean((content.error_text || '').trim()) ||
          outgoing.some((e) => e.sourceHandle === 'error' || e.label === 'error');
        if (!hasErrorPath) {
          warnings.push({ nodeId: node.id, message: `API Call node "${truncateLabel(content.url)}" has no error reply or error edge.` });
        }
      }
    }

    if (type === 'input' && !String(content.field_name || '').trim()) {
      warnings.push({ nodeId: node.id, message: 'An input node has no field_name — collected value will not be stored.' });
    }

    if (type !== 'end' && type !== 'api_call' && !String(content.text || '').trim()) {
      warnings.push({ nodeId: node.id, message: `A ${type} node has no message text.` });
    }

    if (type !== 'end' && outgoing.length === 0) {
      warnings.push({ nodeId: node.id, message: `A ${type} node has no outgoing edges — the conversation will dead-end here.` });
    }
  });

  return { errors, warnings };
};

const truncateLabel = (text = '', max = 40) =>
  String(text || '').length > max ? String(text).slice(0, max) + '…' : String(text || text);

const getBotApiStatusCode = (res) => Number(res?.data?.statuscode ?? res?.data?.statusCode);

const getBotApiErrorMessage = (error, fallback = 'Request failed') => (
  error?.response?.data?.message
  || error?.data?.message
  || error?.message
  || fallback
);

const assertBotApiSuccess = (res) => {
  const code = getBotApiStatusCode(res);
  const failed = (Number.isFinite(code) && code >= 400) || res?.data?.status === false;
  if (!failed) return res;
  const error = new Error(res?.data?.message || 'Request failed');
  error.response = { data: res?.data };
  throw error;
};

// Key-order-insensitive JSON stringify — prevents false "Save did not persist" from
// structural differences (key ordering, defaults merging) unrelated to actual content.
const stableStringify = (val) => JSON.stringify(val, (_, v) => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {});
  }
  return v;
});

const unwrapBotResponse = (res) => {
  assertBotApiSuccess(res);
  return res.data?.entity || res.data?.payload || res.data?.data || res.data;
};

const isApiCallUnsupportedError = (error) => {
  const message = getBotApiErrorMessage(error, '').toLowerCase();
  return message.includes('node_type') && message.includes('message') && message.includes('ai_handoff');
};

/** Generate a stable option id like opt_<5char> */
const newOptionId = () => 'opt_' + Math.random().toString(36).slice(2, 7);

const FlowBuilderInner = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();

  const [flow, setFlow] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [draftContent, setDraftContent] = useState(null);   // local edits to selected node's content
  const [draftIsStart, setDraftIsStart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const positionTimers = useRef({});
  const { fitView } = useReactFlow();
  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );

  const availableVariables = useMemo(() => {
    const vars = new Set(['user_phone']);
    nodes.forEach((node) => {
      const content = node.data?.content || {};
      if (node.data?.node_type === 'input' && content.field_name) {
        vars.add(content.field_name);
      }
      if (node.data?.node_type === 'api_call' && content.response_map) {
        Object.keys(content.response_map).forEach((key) => vars.add(key));
      }
    });
    return Array.from(vars);
  }, [nodes]);

  const didInitialLoad = useRef(false);
  const reload = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const flowRes = await botFlowApi.getFlow(flowId);
      const f = unwrapBotResponse(flowRes);
      const flowNodesFromFlow = extractList(f, ['nodes', 'flow_nodes', 'flowNodes', 'bot_nodes', 'botNodes']);
      const flowEdgesFromFlow = extractList(f, ['edges', 'flow_edges', 'flowEdges']);
      const [nodesRes, edgesRes] = await Promise.all([
        botFlowApi.listNodes(flowId).then((res) => unwrapBotResponse(res)).catch(() => null),
        botFlowApi.listEdges(flowId).then((res) => unwrapBotResponse(res)).catch(() => null),
      ]);
      const nodesFromEndpoint = extractList(nodesRes, ['nodes', 'flow_nodes', 'flowNodes', 'bot_nodes', 'botNodes']);
      const edgesFromEndpoint = extractList(edgesRes, ['edges', 'flow_edges', 'flowEdges']);
      const ns = nodesFromEndpoint.length > 0 ? nodesFromEndpoint : flowNodesFromFlow;
      const es = edgesFromEndpoint.length > 0 ? edgesFromEndpoint : flowEdgesFromFlow;
      const flowNodes = ns
        .map((node, index) => toFlowNode(node, { position: { x: 100 + index * 40, y: 100 + index * 40 } }))
        .filter(Boolean);
      const nodeIds = new Set(flowNodes.map((node) => node.id));
      const flowEdges = es
        .map((edge, index) => toFlowEdge(edge, index))
        .filter((edge) => edge && nodeIds.has(edge.source) && nodeIds.has(edge.target));
      setFlow(f);
      setNodes(flowNodes);
      setEdges(flowEdges);
      if (!didInitialLoad.current) {
        setTimeout(() => fitViewRef.current?.({ padding: 0.2 }), 100);
        didInitialLoad.current = true;
      }
      return { flow: f, nodes: flowNodes, edges: flowEdges };
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load flow');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [flowId]);

  useEffect(() => { reload(); }, [reload]);

  // Sync draft when selection changes
  useEffect(() => {
    if (selectedNode) {
      setDraftContent(cloneJson(selectedNode.data.content || {}));
      setDraftIsStart(!!selectedNode.data.is_start);
    } else {
      setDraftContent(null);
      setDraftIsStart(false);
    }
  }, [selectedNodeId]); // eslint-disable-line

  const saveGraphSnapshot = useCallback(async (nextNodes, nextEdges) => {
    const graphNodes = ensureOneStartNode(nextNodes);
    const payload = {
      flow: toGraphFlowPayload(flow || {}),
      nodes: graphNodes.map(toGraphNodePayload),
      edges: nextEdges.map(toGraphEdgePayload),
    };
    const res = await botFlowApi.saveGraph(flowId, payload).then(assertBotApiSuccess);
    const savedGraph = unwrapBotResponse(res);
    const rawNodes = extractList(savedGraph, ['nodes', 'flow_nodes', 'flowNodes', 'bot_nodes', 'botNodes']);
    const rawEdges = extractList(savedGraph, ['edges', 'flow_edges', 'flowEdges']);
    if (!rawNodes.length) {
      return reload({ silent: true });
    }
    const flowNodes = rawNodes
      .map((node, index) => toFlowNode(node, { position: { x: 100 + index * 40, y: 100 + index * 40 } }))
      .filter(Boolean);
    const nodeIds = new Set(flowNodes.map((node) => node.id));
    const flowEdges = rawEdges
      .map((edge, index) => toFlowEdge(edge, index))
      .filter((edge) => edge && nodeIds.has(edge.source) && nodeIds.has(edge.target));
    setFlow((currentFlow) => ({ ...(currentFlow || {}), ...(savedGraph || {}) }));
    setNodes(flowNodes);
    setEdges(flowEdges);
    return { flow: savedGraph, nodes: flowNodes, edges: flowEdges };
  }, [flow, flowId, reload]);

  // ─── React Flow event handlers ────────────────────────────────────────
  const onNodesChange = useCallback((changes) => {
    setNodes((curr) => applyNodeChanges(changes, curr));
    // Persist position only when drag ends (single API call per drag, debounced).
    changes.forEach((c) => {
      if (c.type === 'position' && c.position && c.dragging === false) {
        clearTimeout(positionTimers.current[c.id]);
        positionTimers.current[c.id] = setTimeout(() => {
          botFlowApi.updateNode(flowId, c.id, { position: c.position }).catch(() => {});
        }, 800);
      }
    });
  }, [flowId]);

  const onEdgesChange = useCallback((changes) => {
    setEdges((curr) => applyEdgeChanges(changes, curr));
  }, []);

  const onConnect = useCallback(async (connection) => {
    try {
      const condition = connection.sourceHandle && connection.sourceHandle !== 'default'
        ? connection.sourceHandle : null;
      const nextEdge = {
        id: `tmp_edge_${Date.now()}`,
        source: connection.source,
        target: connection.target,
        sourceHandle: condition || 'default',
        label: condition || '',
        ...getEdgeVisual(condition),
      };
      await saveGraphSnapshot(nodes, addEdge(nextEdge, edges));
      setNotice(null);
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Edge create failed');
      setNotice({ type: 'error', title: 'Connection was not created', message });
    }
  }, [edges, nodes, saveGraphSnapshot]);

  const onEdgeDelete = useCallback(async (edge) => {
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Delete connection',
        content: 'Remove this connection between nodes?',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    try {
      await saveGraphSnapshot(nodes, edges.filter((e) => e.id !== edge.id));
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Delete edge failed');
      setNotice({ type: 'error', title: 'Connection was not deleted', message });
    }
  }, [edges, nodes, saveGraphSnapshot]);

  // ─── Palette: add new node ────────────────────────────────────────────
  const addNode = async (node_type) => {
    try {
      const position = { x: 200 + Math.random() * 200, y: 200 + Math.random() * 200 };
      const res = await botFlowApi.createNode(flowId, {
        node_type,
        content: NEW_NODE_DEFAULTS[node_type],
        is_start: nodes.length === 0,
        position,
      });
      const n = unwrapBotResponse(res);
      const fallbackNode = {
        id: getFirstDefined(n?.id, n?.node?.id, n?.data?.id, res.data?.id, res.data?.data?.id),
        node_type,
        content: NEW_NODE_DEFAULTS[node_type],
        is_start: nodes.length === 0,
        position,
      };
      const flowNode = toFlowNode(n, fallbackNode);
      if (!flowNode) {
        await reload({ silent: true });
        return;
      }
      setNodes((curr) => [...curr, flowNode]);
      setSelectedNodeId(flowNode.id);
      setNotice(null);
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Add node failed');
      if (node_type === 'api_call' && isApiCallUnsupportedError(e)) {
        setNotice({
          type: 'error',
          title: 'Backend API Call node support is not active',
          message: `The frontend sent node_type "api_call", but the backend rejected it: ${message}. Deploy the backend api_call node update from the provided guide before this node can be created or executed.`,
        });
        return;
      }
      setNotice({ type: 'error', title: 'Node was not created', message });
    }
  };

  // ─── Side panel: save edits ───────────────────────────────────────────
  const saveSelectedNode = async (contentOverride = null) => {
    if (!selectedNode || !draftContent) return;
    const selectedNodeType = normalizeNodeType(selectedNode.data?.node_type);
    const contentToSave = contentOverride || draftContent;
    if (selectedNodeType === 'api_call') {
      const errors = validateApiCallContent(contentToSave);
      if (errors.length) {
        setNotice({ type: 'error', title: 'Validation error', message: errors[0] });
        return;
      }
    }
    if (selectedNodeType === 'message') {
      const options = Array.isArray(contentToSave.options) ? contentToSave.options : [];
      const ids = options.map((opt) => String(opt.id || '').trim()).filter(Boolean);
      if (new Set(ids).size !== ids.length) {
        setNotice({ type: 'error', title: 'Duplicate option IDs', message: 'Button option IDs must be unique. Please fix duplicate IDs before saving.' });
        return;
      }
    }
    setSaving(true);
    try {
      const wasStartChanged = draftIsStart && !selectedNode.data.is_start;
      const nextContent = selectedNodeType === 'api_call'
        ? sanitizeApiCallContent(contentToSave)
        : selectedNodeType === 'message'
          ? withMessageButtonAliases(contentToSave)
          : normalizeNodeContent(selectedNodeType, contentToSave);
      const nextNodes = nodes.map((n) => {
        if (n.id === selectedNode.id) {
          return { ...n, data: { ...n.data, content: nextContent, is_start: draftIsStart } };
        }
        if (wasStartChanged && n.data.is_start) {
          return { ...n, data: { ...n.data, is_start: false } };
        }
        return n;
      });
      const savedContent = nextContent;
      const savedGraph = await saveGraphSnapshot(nextNodes, edges);
      const persistedNode = savedGraph?.nodes?.find((node) => node.id === selectedNode.id);
      if (persistedNode) {
        const persistedContent = persistedNode.data?.content || savedContent;
        setDraftContent(cloneJson(persistedContent));
        setDraftIsStart(!!persistedNode.data?.is_start);
        // Normalize both sides through the same pipeline before comparing, so that
        // key-ordering differences and backend-stripped derived fields (e.g. buttons)
        // don't produce false "Save did not persist" errors.
        const normalizedSaved = normalizeNodeContent(selectedNodeType, savedContent);
        const normalizedPersisted = normalizeNodeContent(selectedNodeType, persistedContent);
        if (stableStringify(normalizedSaved) !== stableStringify(normalizedPersisted)) {
          setNotice({
            type: 'error',
            title: 'Save did not persist',
            message: 'The backend returned older node data after save. Please try again; if it repeats, the backend update API is not accepting the changed content.',
          });
        } else {
          setNotice({ type: 'info', title: 'Node saved', message: 'Your latest node changes are saved.' });
        }
      } else {
        setDraftContent(cloneJson(savedContent));
        setDraftIsStart(draftIsStart);
        setNotice({ type: 'info', title: 'Node saved', message: 'Your latest node changes are saved.' });
      }
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Save failed');
      setNotice({ type: 'error', title: 'Node was not saved', message });
    } finally {
      setSaving(false);
    }
  };

  const deleteSelectedNode = async () => {
    if (!selectedNode) return;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Delete node',
        content: 'Delete this node and all its connections? This cannot be undone.',
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    const idToDelete = selectedNode.id;
    try {
      await saveGraphSnapshot(
        nodes.filter((n) => n.id !== idToDelete),
        edges.filter((e) => e.source !== idToDelete && e.target !== idToDelete),
      );
      setSelectedNodeId(null);
      setNotice({ type: 'info', title: 'Node deleted', message: 'The node and its connections were removed.' });
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Delete failed');
      setNotice({ type: 'error', title: 'Node was not deleted', message });
    }
  };

  // ─── Save All Nodes ─────────────────────────────────────────────────
  const saveAllNodes = async () => {
    setSaving(true);
    try {
      await saveGraphSnapshot(nodes, edges);
      setNotice({ type: 'info', title: 'All nodes saved', message: 'Every node in this flow has been saved.' });
    } catch (e) {
      setNotice({ type: 'error', title: 'Save all failed', message: getBotApiErrorMessage(e, 'Save all failed') });
    } finally {
      setSaving(false);
    }
  };

  // ─── Prettify (auto-layout) ───────────────────────────────────────────
  // Re-arranges every node into a tidy top-down tree using dagre, then
  // persists the new positions to the backend so the layout survives reload.
  const handlePrettify = useCallback(async () => {
    if (!nodes.length) return;
    const laid = getLayoutedElements(nodes, edges, 'TB');
    setNodes(laid);
    // Center the view on the new layout.
    setTimeout(() => {
      try { fitViewRef.current?.({ padding: 0.2, duration: 400 }); } catch {}
    }, 50);
    try {
      await saveGraphSnapshot(laid, edges);
    } catch { /* best-effort */ }
  }, [nodes, edges, saveGraphSnapshot]);

  const repairRuntimeShape = async () => {
    if (!nodes.length) return;
    setSaving(true);
    try {
      await Promise.all(nodes.map((node) => {
        const nodeType = normalizeNodeType(node.data?.node_type);
        if (nodeType === 'message') {
          return botFlowApi.updateNode(flowId, node.id, {
            content: withMessageButtonAliases(node.data?.content || {}),
            is_start: !!node.data?.is_start,
          }).then(assertBotApiSuccess);
        }
        if (nodeType === 'api_call') {
          return botFlowApi.updateNode(flowId, node.id, {
            content: sanitizeApiCallContent(node.data?.content || {}),
            is_start: !!node.data?.is_start,
          }).then(assertBotApiSuccess);
        }
        return Promise.resolve();
      }));
      await reload({ silent: true });
      const silentApiNodes = nodes.filter((node) => {
        if (normalizeNodeType(node.data?.node_type) !== 'api_call') return false;
        const hasSuccessText = Boolean((node.data?.content?.success_text || '').trim());
        const hasSuccessEdge = edges.some((edge) => edge.source === node.id && (edge.sourceHandle === 'success' || edge.label === 'success'));
        return !hasSuccessText && !hasSuccessEdge;
      });
      const silentWarning = silentApiNodes.length > 0
        ? ` Warning: ${silentApiNodes.length} API Call node${silentApiNodes.length > 1 ? 's have' : ' has'} no success text and no connected success handle — it will call the API but send no reply unless you add success text or connect the success handle.`
        : '';
      setNotice({
        type: silentApiNodes.length > 0 ? 'error' : 'info',
        title: 'Runtime shape repaired',
        message: `Message buttons now include both options and buttons payload aliases, and API Call nodes were re-saved in the backend-safe format.${silentWarning}`,
      });
    } catch (error) {
      setNotice({
        type: 'error',
        title: 'Runtime repair failed',
        message: getBotApiErrorMessage(error, 'Could not repair this flow'),
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Activate / back ──────────────────────────────────────────────────
  const toggleActivate = async () => {
    if (!flow) return;
    if (!flow.is_active) {
      // Reuse the shared validator — same rules apply for activation and preview
      const { errors, warnings: _w } = validateFlowForPreview(nodes, edges);
      if (errors.length > 0) {
        const first = errors[0];
        if (first.nodeId) setSelectedNodeId(first.nodeId);
        setNotice({ type: 'error', title: 'Fix before activating', message: first.message });
        return;
      }
      // Additional activation-specific guard: warn about missing success/error paths
      // (validateFlowForPreview already emits these as warnings; escalate to hard block for activation)
      const apiNodes = nodes.filter((n) => n.data?.node_type === 'api_call');
      const apiNodeWithoutSuccess = apiNodes.find((node) => {
        const hasSuccessText = Boolean((node.data?.content?.success_text || '').trim());
        const hasSuccessEdge = edges.some((e) => e.source === node.id && (e.sourceHandle === 'success' || e.label === 'success'));
        return !hasSuccessText && !hasSuccessEdge;
      });
      if (apiNodeWithoutSuccess) {
        setNotice({ type: 'error', title: 'API Call missing success reply', message: 'Each API Call node needs either a success message or a connected success handle.' });
        setSelectedNodeId(apiNodeWithoutSuccess.id);
        return;
      }
      const apiNodeWithoutError = apiNodes.find((node) => {
        const hasErrorText = Boolean((node.data?.content?.error_text || '').trim());
        const hasErrorEdge = edges.some((e) => e.source === node.id && (e.sourceHandle === 'error' || e.label === 'error'));
        return !hasErrorText && !hasErrorEdge;
      });
      if (apiNodeWithoutError) {
        setNotice({ type: 'error', title: 'API Call missing error reply', message: 'Each API Call node should have either error text or a connected error handle. Add one before activating.' });
        setSelectedNodeId(apiNodeWithoutError.id);
        return;
      }
    }
    try {
      if (flow.is_active) await botFlowApi.deactivate(flow.id).then(assertBotApiSuccess);
      else                 await botFlowApi.activate(flow.id).then(assertBotApiSuccess);
      const res = await botFlowApi.getFlow(flow.id);
      setFlow(unwrapBotResponse(res));
      setNotice(null);
    } catch (e) {
      const message = getBotApiErrorMessage(e, 'Activate failed');
      setNotice({ type: 'error', title: 'Flow status was not changed', message });
    }
  };

  if (loading) return <div>Loading flow...</div>;
  if (err) return <div style={{ color: '#dc2626' }}>Error: {err}</div>;
  if (!flow) return <div>Flow not found</div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="builder-topbar">
        <div className="builder-topbar-left">
          <button className="btn btn-secondary builder-back-btn" onClick={() => navigate('/dashboard/bot-flow')}>
            <ArrowLeftOutlined /> Back
          </button>
          <h2 className="builder-flow-heading">
            {flow.name}
            {flow.is_active && <span className="badge-active">ACTIVE</span>}
          </h2>
        </div>
        <div className="builder-topbar-right">
          <button
            className="btn btn-primary"
            onClick={saveAllNodes}
            disabled={saving || nodes.length === 0}
            title="Save every node in this flow at once"
          >
            <SaveOutlined /> {saving ? 'Saving…' : 'Save All'}
          </button>
          <button
            className={`btn ${showPreview ? 'btn-preview-active' : 'btn-preview'}`}
            onClick={() => {
              if (showPreview) {
                setShowPreview(false);
              } else {
                setShowPreview(true);
                setSidebarOpen(true);
              }
            }}
            disabled={nodes.length === 0}
            title={showPreview ? 'Exit preview' : 'Simulate end-user experience before activating'}
          >
            <PlayCircleOutlined /> {showPreview ? 'Exit Preview' : 'Preview'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrettify}
            title="Auto-arrange nodes into a clean top-down layout"
          >
            <ThunderboltOutlined /> Prettify
          </button>
          <button
            className="btn btn-secondary"
            onClick={repairRuntimeShape}
            disabled={saving || nodes.length === 0}
            title="Re-save message/API nodes in the backend runtime-compatible shape"
          >
            <SaveOutlined /> Repair Runtime Shape
          </button>
          <button className="btn btn-secondary" onClick={toggleActivate}>
            {flow.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {notice && (
        <div className={`builder-notice ${notice.type || 'info'}`}>
          <div>
            <strong>{notice.title}</strong>
            <span>{notice.message}</span>
          </div>
          <button type="button" onClick={() => setNotice(null)}>x</button>
        </div>
      )}

      <div className="flow-builder">
        <div className="flow-builder-canvas">
          {nodes.length === 0 && (
            <div className="flow-empty-state">
              <strong>No nodes in this flow yet</strong>
              <span>Add a node from the right panel to start building this flow.</span>
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeDoubleClick={(_, e) => onEdgeDelete(e)}
            onNodeClick={(_, n) => setSelectedNodeId(n.id)}
            onPaneClick={() => setSelectedNodeId(null)}
            fitView
            deleteKeyCode={null}
          >
            <Background gap={16} color="#e5e7eb" />
            <MiniMap pannable zoomable />
            <Controls />
          </ReactFlow>
        </div>

        <div className={`flow-builder-side-wrap${sidebarOpen ? '' : ' collapsed'}`}>
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            title={sidebarOpen ? 'Collapse panel' : 'Expand panel'}
          >
            {sidebarOpen ? '›' : '‹'}
          </button>

          <aside className="flow-builder-side" style={showPreview ? { padding: 0, overflow: 'hidden' } : {}}>
            {showPreview ? (
              <FlowPreviewPanel
                onClose={() => setShowPreview(false)}
                nodes={nodes}
                edges={edges}
                hasDraftChanges={Boolean(
                  selectedNodeId &&
                  draftContent &&
                  JSON.stringify(draftContent) !==
                    JSON.stringify(nodes.find((n) => n.id === selectedNodeId)?.data?.content),
                )}
                onPreviewApiCall={(body) => botFlowApi.previewApiCall(body)}
                validateFlow={validateFlowForPreview}
              />
            ) : (
              <>
            <h4>Add Node</h4>
            <button className="palette-btn message"  onClick={() => addNode('message')}>
              <MessageOutlined className="icon" /> Message + Buttons
            </button>
            <button className="palette-btn input"    onClick={() => addNode('input')}>
              <FormOutlined className="icon" /> Ask for Input
            </button>
            <button className="palette-btn handoff"  onClick={() => addNode('ai_handoff')}>
              <RobotOutlined className="icon" /> Talk to AI (handoff)
            </button>
            <button
              className="palette-btn api_call"
              onClick={() => addNode('api_call')}
              title="Requires backend support for node_type api_call"
            >
              <GlobalOutlined className="icon" /> API Call
            </button>
            <button className="palette-btn end"      onClick={() => addNode('end')}>
              <StopOutlined className="icon" /> End
            </button>

            <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

            {!selectedNode && (
              <div style={{ color: '#6b7280', fontSize: 12 }}>
                Click a node to edit its content, or drag from the dot on a button to connect to another node.
                <br /><br />
                Double-click an edge to delete it.
              </div>
            )}

            {selectedNode && draftContent && (
              <NodeEditor
                node={selectedNode}
                draftContent={draftContent}
                setDraftContent={setDraftContent}
                draftIsStart={draftIsStart}
                setDraftIsStart={setDraftIsStart}
                onSave={saveSelectedNode}
                onDelete={deleteSelectedNode}
                saving={saving}
                availableVariables={availableVariables}
              />
            )}
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

const VariableAwareField = ({ value, onChange, variables, multiline = false, placeholder = '', rows = 3 }) => {
  const [showPicker, setShowPicker] = useState(false);
  const fieldRef = useRef(null);
  const Field = multiline ? 'textarea' : 'input';

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const cursor = event.target.selectionStart || nextValue.length;
    onChange(nextValue);
    setShowPicker(nextValue.slice(Math.max(0, cursor - 2), cursor) === '{{');
  };

  const insertVariable = (variable) => {
    const currentValue = value || '';
    const field = fieldRef.current;
    const cursor = field?.selectionStart ?? currentValue.length;
    const beforeCursor = currentValue.slice(0, cursor).replace(/\{\{$/, '');
    const afterCursor = currentValue.slice(cursor);
    const token = `{{${variable}}}`;
    const nextValue = `${beforeCursor}${token}${afterCursor}`;
    onChange(nextValue);
    setShowPicker(false);
    setTimeout(() => {
      const nextCursor = beforeCursor.length + token.length;
      fieldRef.current?.focus();
      fieldRef.current?.setSelectionRange(nextCursor, nextCursor);
    }, 0);
  };

  return (
    <div className="variable-field-wrap">
      <Field
        ref={fieldRef}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        rows={multiline ? rows : undefined}
      />
      {showPicker && variables.length > 0 && (
        <div className="variable-picker">
          {variables.map((variable) => (
            <button key={variable} type="button" onClick={() => insertVariable(variable)}>
              {`{{${variable}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const KeyValueEditor = ({ value, onChange, variables = [], keyPlaceholder = 'key', valuePlaceholder = 'value', valueUsesVariables = false, blockedKeys = [] }) => {
  const [rows, setRows] = useState(normalizeKeyValueRows(objectToRows(value)));
  const valueSignature = JSON.stringify(value || {});

  useEffect(() => {
    setRows(normalizeKeyValueRows(objectToRows(value)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSignature]);

  const commitRows = (nextRows) => {
    const normalizedRows = normalizeKeyValueRows(nextRows);
    setRows(normalizedRows);
    onChange(rowsToObject(normalizedRows));
  };

  const updateRow = (index, patch) => {
    const nextRows = rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row);
    commitRows(nextRows);
  };
  const removeRow = (index) => commitRows(rows.filter((_, rowIndex) => rowIndex !== index));
  const addRow = () => commitRows([...rows, { key: '', value: '' }]);

  return (
    <div className="kv-editor">
      {rows.length === 0 && <div className="empty-hint">No rows yet.</div>}
      {rows.map((row, index) => (
        <React.Fragment key={index}>
          <div className="kv-row">
            <input
              value={row.key}
              onChange={(event) => updateRow(index, { key: event.target.value })}
              placeholder={keyPlaceholder}
            />
            {valueUsesVariables ? (
              <VariableAwareField
                value={row.value}
                onChange={(nextValue) => updateRow(index, { value: nextValue })}
                variables={variables}
                placeholder={valuePlaceholder}
              />
            ) : (
              <input
                value={row.value}
                onChange={(event) => updateRow(index, { value: event.target.value })}
                placeholder={valuePlaceholder}
              />
            )}
            <button className="btn-tiny" type="button" onClick={() => removeRow(index)}>x</button>
          </div>
          {blockedKeys.length > 0 && row.key && blockedKeys.includes(row.key.toLowerCase()) && (
            <div className="kv-blocked-warning">
              ⚠️ <strong>{row.key}</strong> is blocked. Use the <strong>Authentication</strong> section above to send tokens — do not put them in headers.
            </div>
          )}
        </React.Fragment>
      ))}
      <button className="btn btn-secondary add-row-btn" type="button" onClick={addRow}>
        <PlusOutlined /> Add row
      </button>
    </div>
  );
};

const toVariableName = (value, fallback = 'value') => {
  const cleaned = String(value || fallback)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  return cleaned || fallback;
};

const LABEL_STRIP_PREFIX = /^(template|meta|library_template_)/i;
const toReadableLabel = (value) => {
  const cleaned = String(value || 'Value').replace(LABEL_STRIP_PREFIX, '') || String(value || 'Value');
  return cleaned
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPrimitivePreview = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.find((item) => item !== null && typeof item !== 'object') || '';
  if (typeof value === 'object') return '';
  return String(value);
};

const findFieldByNames = (fields, names) => fields.find((field) =>
  names.some((name) => {
    const kl = field.key.toLowerCase();
    return kl === name || kl.endsWith(name) || kl.startsWith(name + '_');
  })
);

const getCollectionIntro = (collection) => {
  if (/plan|pricing|price|subscription/.test(collection)) return 'Here are our current plans:';
  if (/product|item|catalog/.test(collection)) return 'Here are the products I found:';
  if (/template/.test(collection)) return 'Here are the templates I found:';
  if (/order|booking|ticket/.test(collection)) return 'Here are the order details:';
  if (/user|customer|contact/.test(collection)) return 'Here is the customer information:';
  return 'Here are the details I found:';
};

const findObjectArrays = (value, path = '') => {
  if (Array.isArray(value)) {
    const objectItems = value.filter((item) => isPlainObject(item));
    const nested = value.flatMap((item, index) => findObjectArrays(item, path ? `${path}.${index}` : String(index)));
    return objectItems.length > 0 ? [{ path, items: objectItems }, ...nested] : nested;
  }
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, child]) => findObjectArrays(child, path ? `${path}.${key}` : key));
  }
  return [];
};

const findLeafFields = (value, path = '', limit = 8, result = []) => {
  if (result.length >= limit) return result;
  if (Array.isArray(value)) {
    value.slice(0, 3).forEach((item, index) => findLeafFields(item, path ? `${path}.${index}` : String(index), limit, result));
    return result;
  }
  if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, child]) => findLeafFields(child, path ? `${path}.${key}` : key, limit, result));
    return result;
  }
  if (path) result.push({ path, key: path.split('.').pop(), value });
  return result;
};

const SKIP_ITEM_KEYS = /id$|^id$|rowno|component|content$|file_path|^source$|library_template|created_at|updated_at|deleted_at/i;
const CONTAINS_PRIORITY = ['name', 'title', 'label', 'price', 'amount', 'status', 'category', 'language', 'type', 'credit', 'feature', 'description'];
const EXACT_PRIORITY = ['display_name', 'name', 'title', 'label', 'monthly_price', 'price', 'amount', 'total', 'credits', 'credit', 'status', 'category', 'description', 'features'];

const chooseFieldsForItem = (item) => {
  const scoreKey = (key) => {
    const kl = key.toLowerCase();
    const exactIdx = EXACT_PRIORITY.indexOf(kl);
    if (exactIdx !== -1) return exactIdx;
    const containsIdx = CONTAINS_PRIORITY.findIndex((p) => kl.includes(p));
    if (containsIdx !== -1) return 100 + containsIdx;
    return 999;
  };
  const fields = Object.entries(item || {})
    .filter(([key]) => !SKIP_ITEM_KEYS.test(key))
    .map(([key, value]) => {
      let path = key;
      if (Array.isArray(value)) {
        const firstPrimitiveIndex = value.findIndex((entry) => entry !== null && typeof entry !== 'object');
        if (firstPrimitiveIndex >= 0) path = `${key}.${firstPrimitiveIndex}`;
      }
      return { key, path, value: getPrimitivePreview(value) };
    })
    .filter((field) => field.value !== '');
  return [...fields].sort((a, b) => scoreKey(a.key) - scoreKey(b.key)).slice(0, 4);
};

const buildSmartReply = (data) => {
  const response_map = {};
  const arrays = findObjectArrays(data)
    .filter((entry) => entry.items.length > 0)
    .sort((a, b) => b.items.length - a.items.length);

  if (arrays.length > 0) {
    const best = arrays[0];
    const collection = toVariableName(best.path.split('.').filter(Boolean).pop() || 'item', 'item');
    const singular = collection.endsWith('s') ? collection.slice(0, -1) : collection;
    const lines = [getCollectionIntro(collection)];

    best.items.slice(0, 3).forEach((item, index) => {
      const fields = chooseFieldsForItem(item);
      const makeToken = (field) => {
        const key = `${singular}_${index + 1}_${toVariableName(field.key)}`;
        response_map[key] = best.path ? `${best.path}.${index}.${field.path}` : `${index}.${field.path}`;
        return `{{${key}}}`;
      };
      const nameField = findFieldByNames(fields, ['display_name', 'name', 'title', 'label']);
      const priceField = fields.find((field) => /price|amount|total/i.test(field.key));
      const creditField = fields.find((field) => /credit/i.test(field.key));
      const featureField = fields.find((field) => /feature|description|detail/i.test(field.key));
      const statusField = fields.find((field) => /status/i.test(field.key));
      const nameText = nameField ? makeToken(nameField) : `${toReadableLabel(singular)} ${index + 1}`;

      if (priceField || creditField || featureField) {
        const details = [];
        if (priceField) details.push(`Rs. ${makeToken(priceField)}/month`);
        if (creditField) details.push(`${makeToken(creditField)} credits`);
        lines.push(`${index + 1}. ${nameText}: ${details.join(' with ') || 'Available'}.`);
        if (featureField) lines.push(`   Includes: ${makeToken(featureField)}`);
        return;
      }

      if (statusField) {
        const extraParts = fields
          .filter((f) => f !== nameField && f !== statusField)
          .slice(0, 2)
          .map((f) => `${toReadableLabel(f.key)}: ${makeToken(f)}`);
        const parts = [`${toReadableLabel(statusField.key)}: ${makeToken(statusField)}`, ...extraParts].join(' | ');
        lines.push(`${index + 1}. ${nameText} — ${parts}`);
        return;
      }

      const otherParts = fields
        .filter((field) => field !== nameField)
        .slice(0, 3)
        .map((field) => `${toReadableLabel(field.key)}: ${makeToken(field)}`);
      if (otherParts.length > 0) lines.push(`${index + 1}. ${nameText} - ${otherParts.join(' - ')}`);
    });

    return {
      response_map,
      success_text: lines.length > 1 ? lines.join('\n') : 'I found the requested details, but could not format them automatically.',
    };
  }

  const fields = findLeafFields(data, '', 6);
  const lines = ['Here are the details I found:'];
  fields.forEach((field) => {
    const key = toVariableName(field.path);
    response_map[key] = field.path;
    lines.push(`${toReadableLabel(field.key)}: {{${key}}}`);
  });
  return {
    response_map,
    success_text: lines.length > 1 ? lines.join('\n') : 'I found the requested details.',
  };
};

const applyAuthToRequest = (url, headers = {}, auth = {}) => {
  const normalized = sanitizeApiAuth(auth);
  const nextHeaders = { ...(headers || {}) };
  let nextUrl = url;
  if (normalized.type === 'bearer' && normalized.token) {
    nextHeaders.Authorization = `Bearer ${normalized.token}`;
  }
  if (normalized.type === 'basic' && normalized.username && normalized.password) {
    nextHeaders.Authorization = `Basic ${btoa(`${normalized.username}:${normalized.password}`)}`;
  }
  if (normalized.type === 'api_key' && normalized.key && normalized.value) {
    if (normalized.in === 'query') {
      const parsedUrl = new URL(nextUrl);
      parsedUrl.searchParams.set(normalized.key, normalized.value);
      nextUrl = parsedUrl.toString();
    } else if (!BLOCKED_HEADER_KEYS.includes(normalized.key.toLowerCase())) {
      nextHeaders[normalized.key] = normalized.value;
    }
  }
  return { url: nextUrl, headers: nextHeaders };
};

const SmartApiReplyBuilder = ({ url, method, auth, headers, body, onApply }) => {
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [preview, setPreview] = useState('');
  const [rawJson, setRawJson] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const generateReply = async () => {
    const cleanUrl = String(url || '').replace(/\{\{\s*[-\w.]+\s*\}\}/g, 'PLACEHOLDER');
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      setErrorMsg('Enter a valid HTTP/HTTPS URL first.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');
    setPreview('');
    setRawJson(null);
    setShowRaw(false);
    try {
      const allowedHeaders = Object.entries(headers || {}).reduce((acc, [k, v]) => {
        const kl = k.toLowerCase();
        if (!['host', 'x-forwarded-for', 'x-real-ip'].includes(kl)) {
          acc[k] = v;
        }
        return acc;
      }, {});

      const authedRequest = applyAuthToRequest(cleanUrl, allowedHeaders, auth);
      const response = await botFlowApi.previewApiCall({
        url: authedRequest.url,
        method: method || 'GET',
        headers: authedRequest.headers,
        body,
      });
      const payload = response?.data || {};
      const parsed = payload.data ?? payload.entity ?? payload;

      setRawJson(parsed);

      // Check application-level errors embedded in a 200 response body
      if (parsed && typeof parsed === 'object') {
        const appCode = Number(parsed.Statuscode ?? parsed.statuscode ?? parsed.status_code ?? 0);
        const appMsg = String(parsed.Message ?? parsed.message ?? '');
        const isAppError = (appCode >= 400 && appCode < 600) || (appCode !== 0 && appCode !== 200 && appCode !== 201);
        if (isAppError) {
          setStatus('error');
          setErrorMsg(`API replied: "${appMsg || `error code ${appCode}`}"`);
          return;
        }
      }

      const generated = buildSmartReply(parsed);
      onApply(generated);
      setPreview(generated.success_text);
      setStatus('success');
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.response?.data?.message || e.message || 'Could not call this API.');
    }
  };

  return (
    <div className="smart-reply-panel">
      <div className="smart-reply-copy">
        <strong>Generate the customer message</strong>
        <span>Paste the API URL and headers, click Generate, and we will create the reply text for the customer.</span>
      </div>
      <div className="smart-reply-actions">
        <button
          className="btn btn-primary smart-reply-btn"
          type="button"
          onClick={generateReply}
          disabled={status === 'loading'}
        >
          {status === 'loading' ? <><LoadingOutlined /> Generating...</> : <><PlayCircleOutlined /> Generate Reply</>}
        </button>
      </div>

      {status === 'success' && (
        <div className="smart-reply-success"><CheckOutlined /> Message created below. You can edit it, then save.</div>
      )}
      {preview && <pre className="smart-reply-preview">{preview}</pre>}
      {status === 'error' && <div className="smart-reply-error">{errorMsg}</div>}

      {rawJson !== null && (
        <div className="raw-response-section">
          <button
            type="button"
            className="raw-response-toggle"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? '▾ Hide' : '▸ Show'} full API response JSON
          </button>
          {showRaw && (
            <>
              <div className="field-hint" style={{ marginBottom: 4 }}>
                Use the dot-path of any field as the value in <strong>Response Field Mapping</strong>. e.g. <code>data.0.name</code>
              </div>
              <pre className="raw-response-json">{JSON.stringify(rawJson, null, 2)}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ApiAuthEditor = ({ auth = {}, onChange, variables }) => {
  const normalized = sanitizeApiAuth(auth);
  const patchAuth = (patch) => onChange(sanitizeApiAuth({ ...normalized, ...patch }));

  return (
    <div className="api-auth-editor">
      <div className="field-row">
        <label>Authentication</label>
        <select
          value={normalized.type}
          onChange={(event) => {
            const type = event.target.value;
            if (type === 'bearer') onChange({ type, token: '' });
            else if (type === 'basic') onChange({ type, username: '', password: '' });
            else if (type === 'api_key') onChange({ type, in: 'header', key: 'X-API-Key', value: '' });
            else onChange({ type: 'none' });
          }}
        >
          <option value="none">No auth</option>
          <option value="bearer">Bearer token</option>
          <option value="api_key">API key</option>
          <option value="basic">Basic auth</option>
        </select>
        <div className="field-hint">If the API says “No token provided”, choose Bearer token or API key here.</div>
      </div>

      {normalized.type === 'bearer' && (
        <div className="field-row">
          <label>Bearer token</label>
          <VariableAwareField
            value={normalized.token || ''}
            onChange={(token) => patchAuth({ token })}
            variables={variables}
            placeholder="YOUR_API_TOKEN"
          />
        </div>
      )}

      {normalized.type === 'api_key' && (
        <div className="api-auth-grid">
          <div className="field-row">
            <label>Send in</label>
            <select value={normalized.in || 'header'} onChange={(event) => patchAuth({ in: event.target.value })}>
              <option value="header">Header</option>
              <option value="query">Query string</option>
            </select>
          </div>
          <div className="field-row">
            <label>Key name</label>
            <input
              value={normalized.key || ''}
              onChange={(event) => patchAuth({ key: event.target.value })}
              placeholder={normalized.in === 'query' ? 'access_token' : 'X-API-Key'}
            />
          </div>
          <div className="field-row api-auth-wide">
            <label>Key value</label>
            <VariableAwareField
              value={normalized.value || ''}
              onChange={(value) => patchAuth({ value })}
              variables={variables}
              placeholder="YOUR_API_KEY"
            />
          </div>
        </div>
      )}

      {normalized.type === 'basic' && (
        <div className="api-auth-grid">
          <div className="field-row">
            <label>Username</label>
            <input value={normalized.username || ''} onChange={(event) => patchAuth({ username: event.target.value })} />
          </div>
          <div className="field-row">
            <label>Password</label>
            <input type="password" value={normalized.password || ''} onChange={(event) => patchAuth({ password: event.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
};

const ApiCallEditor = ({ draftContent, setDraftContent, onSave, saving, availableVariables }) => {
  const method = String(draftContent.method || 'GET').toUpperCase();
  const variableOptions = useMemo(() => Array.from(new Set([
    ...availableVariables,
    ...Object.keys(draftContent.response_map || {}),
  ])), [availableVariables, draftContent.response_map]);
  const [bodyText, setBodyText] = useState(JSON.stringify(draftContent.body || {}, null, 2));
  const [bodyError, setBodyError] = useState('');
  const [bodyMode, setBodyMode] = useState('kv'); // 'kv' | 'json'
  const [bodyKvRows, setBodyKvRows] = useState(() => objectToRows(draftContent.body || {}));

  useEffect(() => {
    setBodyText(JSON.stringify(draftContent.body || {}, null, 2));
    setBodyKvRows(objectToRows(draftContent.body || {}));
    setBodyError('');
  }, [draftContent.body]);

  const switchBodyMode = (nextMode) => {
    if (nextMode === bodyMode) return;
    if (nextMode === 'kv') {
      // JSON → KV: parse current text into rows
      try {
        const parsed = bodyText.trim() ? JSON.parse(bodyText) : {};
        setBodyKvRows(objectToRows(parsed));
        setBodyError('');
      } catch {
        setBodyError('Fix the JSON before switching to key-value mode');
        return;
      }
    } else {
      // KV → JSON: serialise current rows into text
      setBodyText(JSON.stringify(rowsToObject(bodyKvRows), null, 2));
      setBodyError('');
    }
    setBodyMode(nextMode);
  };

  const patchContent = (patch) => setDraftContent({ ...draftContent, ...patch });
  const requestBody = useMemo(() => {
    if (!API_BODY_METHODS.includes(method)) return undefined;
    try {
      return bodyText.trim() ? JSON.parse(bodyText) : {};
    } catch {
      return draftContent.body;
    }
  }, [method, bodyText, draftContent.body]);

  const applySmartReply = (generated) => patchContent({
    response_map: generated.response_map,
    success_text: generated.success_text,
  });

  const handleSave = () => {
    const nextContent = { ...draftContent, method };
    if (API_BODY_METHODS.includes(method)) {
      try {
        let parsedBody;
        if (bodyMode === 'kv') {
          parsedBody = rowsToObject(bodyKvRows);
        } else {
          parsedBody = bodyText.trim() ? JSON.parse(bodyText) : {};
        }
        if (!parsedBody || typeof parsedBody !== 'object' || Array.isArray(parsedBody)) {
          setBodyError('Body must be a JSON object');
          return;
        }
        nextContent.body = parsedBody;
        setBodyError('');
      } catch {
        setBodyError('Body must be valid JSON');
        return;
      }
    } else {
      delete nextContent.body;
    }
    setDraftContent(nextContent);
    onSave(nextContent);
  };

  return (
    <>
      <div className="editor-section">
        <h5>API URL</h5>
        <div className="field-row">
          <label>Paste API URL</label>
          <VariableAwareField
            value={draftContent.url || ''}
            onChange={(url) => patchContent({ url })}
            variables={variableOptions}
            placeholder="https://api.example.com/users/{{user_id}}"
          />
          <div className="field-hint">Use {'{{user_phone}}'} or saved fields only when the API needs a value from the chat.</div>
        </div>

        <div className="field-row compact-method-row">
          <div>
            <label>Method</label>
            <select
              value={method}
              onChange={(event) => patchContent({ method: event.target.value })}
            >
              {API_METHODS.map((apiMethod) => <option key={apiMethod} value={apiMethod}>{apiMethod}</option>)}
            </select>
          </div>
        </div>

        <ApiAuthEditor
          auth={draftContent.auth || { type: 'none' }}
          onChange={(auth) => patchContent({ auth })}
          variables={variableOptions}
        />

        <div className="field-row">
          <label>Headers</label>
          <KeyValueEditor
            value={draftContent.headers || {}}
            onChange={(headers) => patchContent({ headers })}
            variables={variableOptions}
            keyPlaceholder="Cookie"
            valuePlaceholder="token=YOUR_TOKEN_HERE"
            valueUsesVariables
          />
          <div className="field-hint">Add custom headers your API needs (e.g. Cookie, X-API-Key).</div>
        </div>

        <SmartApiReplyBuilder
          url={draftContent.url}
          method={method}
          auth={draftContent.auth}
          headers={draftContent.headers}
          body={requestBody}
          onApply={applySmartReply}
        />
      </div>

      {API_BODY_METHODS.includes(method) && (
        <div className="editor-section">
          <h5>Request Body</h5>
          <div className="field-hint" style={{ marginBottom: 8 }}>
            Sent as <code>Content-Type: application/json</code>. Use <code>{`{{variable}}`}</code> to inject chat values.
          </div>
          <div className="body-mode-toggle">
            <button
              type="button"
              className={`btn-mode${bodyMode === 'kv' ? ' active' : ''}`}
              onClick={() => switchBodyMode('kv')}
            >
              Key-Value
            </button>
            <button
              type="button"
              className={`btn-mode${bodyMode === 'json' ? ' active' : ''}`}
              onClick={() => switchBodyMode('json')}
            >
              Raw JSON
            </button>
          </div>

          {bodyMode === 'kv' ? (
            <KeyValueEditor
              value={rowsToObject(bodyKvRows)}
              onChange={(obj) => setBodyKvRows(objectToRows(obj))}
              variables={variableOptions}
              keyPlaceholder="param1"
              valuePlaceholder="value or {{variable}}"
              valueUsesVariables
            />
          ) : (
            <VariableAwareField
              value={bodyText}
              onChange={setBodyText}
              variables={variableOptions}
              multiline
              rows={7}
              placeholder={'{\n  "phone": "{{user_phone}}",\n  "action": "lookup"\n}'}
            />
          )}
          {bodyError && <div className="field-error">{bodyError}</div>}
        </div>
      )}

      <div className="editor-section">
        <h5>Reply Message</h5>
        <div className="field-row">
          <label>Message sent to user</label>
          <VariableAwareField
            value={draftContent.success_text || ''}
            onChange={(success_text) => patchContent({ success_text })}
            variables={variableOptions}
            multiline
            rows={6}
            placeholder="Click Generate Reply, or write the message here."
          />
          {Object.keys(draftContent.response_map || {}).length > 0 && (
            <div className="field-hint" style={{ marginTop: 4 }}>
              Available tokens — click to copy, then paste into the message:
            </div>
          )}
          {Object.keys(draftContent.response_map || {}).length > 0 && (
            <div className="response-map-tokens">
              {Object.keys(draftContent.response_map).map((key) => (
                <button
                  key={key}
                  type="button"
                  className="map-token-chip"
                  title={`API path: ${draftContent.response_map[key]}`}
                  onClick={() => navigator.clipboard?.writeText(`{{${key}}}`)}
                >
                  {`{{${key}}}`}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="field-row">
          <label>Fallback message if API fails</label>
          <VariableAwareField
            value={draftContent.error_text || ''}
            onChange={(error_text) => patchContent({ error_text })}
            variables={variableOptions}
            multiline
            rows={2}
            placeholder="Sorry, I could not fetch this right now. Please try again later."
          />
        </div>
      </div>

      <div className="editor-section">
        <h5>Response Field Mapping</h5>
        <div className="field-hint" style={{ marginBottom: 8 }}>
          Map API response fields to variable names. Use <code>{`{{variable_name}}`}</code> in your message above.
          After clicking Generate, this fills automatically — or add rows manually.
        </div>
        <KeyValueEditor
          value={draftContent.response_map || {}}
          onChange={(response_map) => patchContent({ response_map })}
          keyPlaceholder="variable_name"
          valuePlaceholder="field path, e.g. 0.name or data.price"
        />
      </div>

      <details className="advanced-api-settings">
        <summary>Advanced settings</summary>

        {API_BODY_METHODS.includes(method) && (
          <div className="field-row">
            <label>Timeout (ms)</label>
            <input
              type="number"
              min="1000"
              max="30000"
              value={draftContent.timeout_ms || 8000}
              onChange={(event) => patchContent({ timeout_ms: Number(event.target.value) })}
              placeholder="8000"
            />
          </div>
        )}
        {!API_BODY_METHODS.includes(method) && (
          <div className="field-row two-col-fields">
            <div>
              <label>Timeout (ms)</label>
              <input
                type="number"
                min="1000"
                max="30000"
                value={draftContent.timeout_ms || 8000}
                onChange={(event) => patchContent({ timeout_ms: Number(event.target.value) })}
                placeholder="8000"
              />
            </div>
          </div>
        )}


      </details>

      <div className="variable-help">
        <strong>Variables:</strong> {variableOptions.map((variable) => `{{${variable}}}`).join(', ')}
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        <SaveOutlined /> {saving ? 'Saving...' : 'Save'}
      </button>
    </>
  );
};

const NodeEditor = ({ node, draftContent, setDraftContent, draftIsStart, setDraftIsStart, onSave, onDelete, saving, availableVariables }) => {
  const type = normalizeNodeType(node.data?.node_type);
  const draftOptions = Array.isArray(draftContent.options) ? draftContent.options : [];
  const setText = (v) => setDraftContent({ ...draftContent, text: v });
  const setField = (v) => setDraftContent({ ...draftContent, field_name: v });
  const setOptions = (opts) => setDraftContent({ ...draftContent, options: opts });
  const editorTitle = formatNodeTypeLabel(type);

  return (
    <div>
      <h4>Edit {editorTitle} Node</h4>

      <div className="field-row">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={draftIsStart} onChange={(e) => setDraftIsStart(e.target.checked)} />
          Start node (entry point of the flow)
        </label>
      </div>

      {type === 'api_call' ? (
        <ApiCallEditor
          draftContent={draftContent}
          setDraftContent={setDraftContent}
          onSave={onSave}
          saving={saving}
          availableVariables={availableVariables}
        />
      ) : (
        <>

      <div className="field-row">
        <label>Message text</label>
        <VariableAwareField
          value={draftContent.text || ''}
          onChange={setText}
          variables={availableVariables}
          multiline
          placeholder="What should the bot say?"
        />
      </div>

      {type === 'input' && (
        <div className="field-row">
          <label>Field name (where to store the answer)</label>
          <input
            value={draftContent.field_name || ''}
            onChange={(e) => setField(e.target.value)}
            placeholder="e.g. name, email, phone"
          />
        </div>
      )}

      {type === 'message' && (
        <div className="field-row">
          <label>Quick-reply buttons (max 3)</label>
          {draftOptions.map((opt, idx) => (
            <div key={idx} className="option-row">
              <input
                value={opt.label}
                placeholder="Button label"
                maxLength={20}
                onChange={(e) => {
                  const next = [...draftOptions];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setOptions(next);
                }}
              />
              <button
                className="btn-tiny"
                onClick={() => setOptions(draftOptions.filter((_, i) => i !== idx))}
              >×</button>
            </div>
          ))}
          {draftOptions.length < 3 && (
            <button
              className="btn btn-secondary"
              style={{ marginTop: 4, fontSize: 12, padding: '4px 10px' }}
              onClick={() => setOptions([...draftOptions, { id: newOptionId(), label: 'New Option' }])}
            >
              <PlusOutlined /> Add option
            </button>
          )}
        </div>
      )}

          <button className="btn btn-primary" onClick={() => onSave()} disabled={saving}>
            <SaveOutlined /> {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}

      <button className="btn btn-danger editor-delete-btn" onClick={onDelete}>
        <DeleteOutlined /> Delete node
      </button>
    </div>
  );
};

const FlowBuilder = () => (
  <ReactFlowProvider>
    <FlowBuilderInner />
  </ReactFlowProvider>
);

export default FlowBuilder;
